/*
 * Author: Joe Wu
 * File Created: Wednesday, 24-08-14 04:51 pm
 * Last Modified: Monday, 25-03-17 03:20 pm, by Harper Yan
 */

import { useRouter } from 'next/router';
import { Trans, useTranslation } from 'next-i18next';

import { Chip, Stack } from '@mui/material';
import { Flex } from '@ucard/ds/containers';

import { useScreenSize } from '@/utils/screen';

import SlideImage from '../../../_components/SlideImage';
import type { ProductConfig } from '../config';
import {
  ChipBox,
  NumberBox,
  ProductButton,
  ProductCardBox,
  ProductImage,
  ProductIntroBox,
  ProductStack,
  ProductSubTitle,
  ProductText,
  ProductTextBox,
  ProductTitle,
} from './style';

interface ProductTransType {
  title: string;
  button: string;
  details: {
    title: string;
    desc: string;
  }[];
}

const Product = (props: ProductConfig) => {
  const {
    imgSrc,
    bgColor,
    titleColor,
    subTitleColor,
    descColor,
    btnTextColor,
    btnColor,
    btnHoverColor,
    btnLink,
    btnCursor,
    i18nKey,
    reverse,
    vip,
    images,
  } = props;

  const { t } = useTranslation('home');
  const router = useRouter();
  const trans: ProductTransType = t(i18nKey, { returnObjects: true });
  const details = trans.details;
  const isXs = useScreenSize('xs');
  const direction = reverse ? 'row-reverse' : 'row';

  if (vip === 'yes') {
    return (
      <Flex
        alignItems="center"
        flexDirection="column"
        gap={[0, 4]}
        py={[0, 10]}>
        <ProductIntroBox>
          <ProductTitle color={titleColor} sx={{ mb: 0 }}>
            <Trans
              components={{ 1: <span className="upscript" /> }}
              i18nKey={`${i18nKey}.title`}
              ns="home"
            />
          </ProductTitle>
        </ProductIntroBox>
        <ProductCardBox>
          <ProductImage
            alt={i18nKey}
            height={430}
            priority
            sizes="100vw"
            src={imgSrc}
            width={582}
          />
          {!isXs && (
            <ProductButton
              sx={{
                color: btnTextColor,
                cursor: btnCursor,
                backgroundColor: btnColor,
                [`&:active,&:hover`]: {
                  color: btnTextColor,
                  backgroundColor: btnColor,
                },
              }}>
              {trans.button}
            </ProductButton>
          )}
        </ProductCardBox>
        {isXs && (
          <ProductButton
            sx={{
              mt: 4,
              color: btnTextColor,
              cursor: btnCursor,
              backgroundColor: btnColor,
              [`&:active,&:hover`]: {
                color: btnTextColor,
                backgroundColor: btnColor,
              },
            }}>
            {trans.button}
          </ProductButton>
        )}
      </Flex>
    );
  }

  return (
    <Flex
      alignItems={['center', 'stretch']}
      flexDirection={isXs ? 'column' : direction}
      gap={[0, 4]}
      py={[0, 10]}>
      <ProductCardBox>
        {/* todo image */}
        {!isXs && <SlideImage images={images} interval={3000} />}
        {!isXs && (
          <ProductButton
            onClick={() => {
              btnLink && router.push(btnLink);
            }}
            sx={{
              color: btnTextColor,
              cursor: btnCursor,
              backgroundColor: btnColor,
              [`&:active,&:hover`]: {
                color: btnTextColor,
                backgroundColor: btnColor,
              },
            }}>
            {trans.button}
          </ProductButton>
        )}
      </ProductCardBox>
      <ProductIntroBox>
        <ProductTitle color={titleColor}>
          <Trans
            components={{
              1: <span className="upscript" />,
              chip: (
                <ChipBox
                  component="span"
                  mb={0.5}
                  ml={1}
                  sx={{
                    fontSize: '18px',
                    lineHeight: '18px',
                    verticalAlign: 'middle',
                  }}
                  vip={vip}
                />
              ),
            }}
            i18nKey={`${i18nKey}.title`}
            ns="home"
          />
        </ProductTitle>
        {details.map((detail, index) => (
          <ProductTextBox key={index}>
            <NumberBox>{index + 1}</NumberBox>
            <Stack>
              <ProductSubTitle color={subTitleColor}>
                <Trans
                  components={{
                    chip: (
                      <ChipBox component="span" ml={0.5} mt={-0.25} vip={vip} />
                    ),
                  }}>
                  {detail.title}
                </Trans>
              </ProductSubTitle>
              <ProductText color={descColor}>{detail.desc}</ProductText>
            </Stack>
          </ProductTextBox>
        ))}
      </ProductIntroBox>
      {isXs && (
        <ProductButton
          onClick={() => {
            btnLink && router.push(btnLink);
          }}
          sx={{
            mt: 4,
            color: btnTextColor,
            cursor: btnCursor,
            backgroundColor: btnColor,
            [`&:active,&:hover`]: {
              color: btnTextColor,
              backgroundColor: btnColor,
            },
          }}>
          {trans.button}
        </ProductButton>
      )}
    </Flex>
  );
};

export default Product;
